import { constrainedPurchaseGuidanceFromThread, constrainedWorkflowChoiceFromThread, hasConstrainedGpcBinding } from './constrainedWorkflowProjection';
import { useCallback, useEffect, useMemo } from 'react';
import useTurnProgress from '../FarChat/useTurnProgress';
import { getThread, HomeChatHttpError, createSession } from './api';
import { normalizeV2Turn } from '../FarChat/api';
import { USER_FACING_ERROR_MESSAGES } from '../../utils/userFacingError';
import type { RawV2Turn } from '../FarChat/api';
import type { ChatSession } from './types';
import { normalizeHomeChatCards } from './types';
import { resetInFlightTurnState, resetTransientTurnState } from '../chatTurnState';
import { HomeChatPanelProps as Props } from './HomeChatPanel.types';
import { isMissingBoundWorkflowError } from './HomeChatTurnErrors';
import { PENDING_ID, coerceAttachments } from './homeChatSessionUtils';
import type { HomeChatPanelState } from './useHomeChatPanelState';
import type { HomeChatHydration } from './useHomeChatHydration';
import type { HomeChatSessionReconciliation } from './useHomeChatSessionReconciliation';
import type { HomeChatTurnSubmission } from './useHomeChatTurnSubmission';


type Inputs = { preserveControllerCards?: boolean } & Pick<HomeChatHydration, 'chatOpenErrorMessage'> &
Required<Pick<Props, 'initialConstructionSowChatView' | 'initialMrChatView' | 'initialMrNotificationBoundResumeId' | 'initialMrObserverNotificationId' | 'workflowOnly'>> &
Pick<HomeChatPanelState, 'setMrNotificationBoundResumeId' | 'setMrObserverNotificationId' | 'setWorkflowOnlyChoice' | 'setWorkflowOnlyEntryMessage' | 'setWorkflowOnlyPurchaseGuidance'> &
Pick<HomeChatPanelState,
  | 'sessionsLoading'
  | 'firstRunRef'
  | 'activeSessionIdRef'
  | 'setGpcChatView'
  | 'setMrChatView'
  | 'setConstructionSowChatView'
  | 'setServicesPwsChatView'
  | 'sessionsRef'
  | 'setRehydratingGpcSessionId'
  | 'setActiveSessionId'
  | 'setMessages'
  | 'setAttachments'
  | 'setTurns'
  | 'setChatCards'
  | 'interactionRevisionRef'
  | 'setBranchRevision'
  | 'restorePendingClarification'
  | 'setSessions'
  | 'cardActionRevisionRef'
  | 'workflowRecommendationRequestIdRef'
  | 'setBusyWorkflowRecommendationCardId'
  | 'setBusySimilarRequirementsCardId'
  | 'clearPendingIntentChoiceSelection'
  | 'setPendingGpcRouter'
  | 'setInFlight'
  | 'setPendingRequestIdSynced'
  | 'setInFlightUserMsg'
  | 'setPendingClarification'
  | 'setInFlightClarification'
  | 'setPendingTopicClarification'
  | 'setInFlightTopicFocus'
  | 'inputContainerRef'
  | 'activeSessionId'
  | 'pendingCreatePromiseRef'
  | 'setPendingRequestId'
  | 'setReplacingTurnId'
  | 'mrChatView'
  | 'pendingIntentChoiceSelection'
  | 'pendingRequestId'
  | 'gpcViewsByHomeSessionRef'
> &
Required<Pick<Props, 'initialMessage' | 'initialSessionId'>> &
Pick<HomeChatHydration,
  | 'hydrateDocumentHistory'
  | 'hydrateMrBinding'
  | 'hydrateServicesPwsBinding'
  | 'hydrateConstructionSowBinding'
  | 'hydrateGpcState'
  | 'notify'
> &
Pick<HomeChatSessionReconciliation,
  | 'clearUnavailableSession'
  | 'refreshSessions'
> &
Pick<HomeChatTurnSubmission,
  | 'runPrimingTurn'
>;

export function useHomeChatSessions({
  preserveControllerCards = false,
  sessionsLoading,
  firstRunRef,
  initialMessage,
  initialSessionId,
  activeSessionIdRef,
  setGpcChatView,
  setMrChatView,
  setConstructionSowChatView,
  setServicesPwsChatView,
  sessionsRef,
  setRehydratingGpcSessionId,
  setActiveSessionId,
  setMessages,
  setAttachments,
  setTurns,
  setChatCards,
  interactionRevisionRef,
  setBranchRevision,
  hydrateMrBinding,
  hydrateDocumentHistory,
  hydrateServicesPwsBinding,
  hydrateConstructionSowBinding,
  restorePendingClarification,
  hydrateGpcState,
  notify,
  clearUnavailableSession,
  runPrimingTurn,
  setSessions,
  cardActionRevisionRef,
  workflowRecommendationRequestIdRef,
  setBusyWorkflowRecommendationCardId,
  setBusySimilarRequirementsCardId,
  clearPendingIntentChoiceSelection,
  setPendingGpcRouter,
  setInFlight,
  setPendingRequestIdSynced,
  setInFlightUserMsg,
  setPendingClarification,
  setInFlightClarification,
  setPendingTopicClarification,
  setInFlightTopicFocus,
  inputContainerRef,
  activeSessionId,
  pendingCreatePromiseRef,
  refreshSessions,
  setPendingRequestId,
  setReplacingTurnId,
  mrChatView,
  pendingIntentChoiceSelection,
  pendingRequestId,
  gpcViewsByHomeSessionRef,
  chatOpenErrorMessage,
  initialConstructionSowChatView,
  initialMrChatView,
  initialMrNotificationBoundResumeId,
  initialMrObserverNotificationId,
  workflowOnly,
  setMrNotificationBoundResumeId,
  setMrObserverNotificationId,
  setWorkflowOnlyChoice,
  setWorkflowOnlyEntryMessage,
  setWorkflowOnlyPurchaseGuidance,
}: Inputs) {

  // One-shot priming for `initialMessage` / `initialSessionId`. Runs after the
  // initial sessions list resolves so the rail is populated before we hydrate
  // or send. `initialSessionId` may arrive later, after workflow metadata is
  // fetched, so it must remain a dependency until priming actually occurs.
  useEffect(() => {
    // Wait for the mount-time sessions fetch to settle. `sessionsLoading`
    // flips to true synchronously inside `refreshSessions`, then back to
    // false on completion.
    if (sessionsLoading) return;
    if (firstRunRef.current) return;
    if (!initialMessage && !initialSessionId) return;
    if (
      initialSessionId
      && activeSessionIdRef.current === initialSessionId
      && !initialMessage
    ) {
      firstRunRef.current = true;
      return;
    }
    firstRunRef.current = true;

    void (async () => {
      // Capture priming inputs locally so callbacks below don't depend on
      // stale closures of React state for `activeSessionId`.
      const sid = initialSessionId;
      const msg = initialMessage;

      if (sid) {
        // 1) Adopt the session locally so the rail/right pane reflect it.
        setGpcChatView(null);
        // A notification Continue response already contains the exact
        // server-issued, owner-bound MR projection. Keep it visible while
        // adopting its Home session; clearing it here briefly replaced a
        // valid generic execution-wait view with an empty workflow shell.
        // The Launcher admits only a canonical pending form or the narrow
        // execution-wait-without-form contract.
        setMrChatView(initialMrChatView || null);
        setMrObserverNotificationId(initialMrObserverNotificationId);
        setMrNotificationBoundResumeId(initialMrNotificationBoundResumeId);
        // The constrained launcher already has the exact, server-issued CE
        // construction controller. Preserve it while adopting the new Home
        // session; otherwise this one-shot priming effect clears the child
        // before the binding hydration request can find a just-created row.
        setConstructionSowChatView(initialConstructionSowChatView || null);
        setServicesPwsChatView(null);
        const knownGpcSession = sessionsRef.current.some((session) => (
          session.session_id === sid && (
            Boolean(session.gpc_binding?.gpc_chat_session_id)
            || hasConstrainedGpcBinding(session)
          )
        ));
        setRehydratingGpcSessionId(knownGpcSession ? sid : null);
        activeSessionIdRef.current = sid;
        setActiveSessionId(sid);
        setMessages([]);
        setAttachments([]);
        setTurns([]);
        setChatCards([]);
        if (initialConstructionSowChatView) {
          // Do not make visibility of this direct continuation depend on a
          // second thread read. The follow-on MR progress/turn routes remain
          // owner-bound and return the canonical form after execution stops.
          return;
        }
        const hydrationRevision = interactionRevisionRef.current;
        const isCurrentHydration = () => (
          interactionRevisionRef.current === hydrationRevision
          && activeSessionIdRef.current === sid
        );
        try {
          const thread = await getThread(sid);
          if (!isCurrentHydration()) return;
          hydrateDocumentHistory(thread, sid);
          const hydratedMessages = thread.messages || [];
          setMessages(hydratedMessages);
          setAttachments(coerceAttachments(thread.attachments));
          const rawTurns = (thread.turns || []) as RawV2Turn[];
          setTurns(rawTurns.map((t) => normalizeV2Turn(t)));
          setBranchRevision(
            Number.isSafeInteger((thread as { branch_revision?: unknown }).branch_revision)
              ? (thread as { branch_revision: number }).branch_revision
              : 0,
          );
          const hasMrBinding = await hydrateMrBinding(
            thread,
            sid,
            isCurrentHydration,
          );
          if (!isCurrentHydration()) return;
          const hasServicesPwsBinding = !hasMrBinding
            && await hydrateServicesPwsBinding(thread, sid, isCurrentHydration);
          if (!isCurrentHydration()) return;
          const hasConstructionSowBinding = !hasMrBinding && !hasServicesPwsBinding
            && await hydrateConstructionSowBinding(
              thread,
              sid,
              isCurrentHydration,
            );
          if (!isCurrentHydration()) return;
          const hasBoundController = hasMrBinding || hasServicesPwsBinding || hasConstructionSowBinding;
          const constrainedChoice = workflowOnly
            ? constrainedWorkflowChoiceFromThread(thread)
            : null;
          const constrainedGuidance = workflowOnly ? constrainedPurchaseGuidanceFromThread(thread) : null;
          if (workflowOnly && thread.constrained_session) {
            setWorkflowOnlyChoice(constrainedChoice?.card || null);
            setWorkflowOnlyPurchaseGuidance(constrainedGuidance?.card || null);
            if (constrainedChoice?.entryMessage || constrainedGuidance?.entryMessage) {
              setWorkflowOnlyEntryMessage(constrainedChoice?.entryMessage || constrainedGuidance?.entryMessage || null);
            }
          }
          setChatCards(
            (hasBoundController && !preserveControllerCards && !hasMrBinding) || (workflowOnly && !hasMrBinding && !constrainedGuidance && !constrainedChoice)
              ? [] : normalizeHomeChatCards(thread.chat_cards),
          );
          restorePendingClarification(thread, sid);
          hydrateGpcState(thread, hasBoundController);
          if (!thread.gpc_chat?.session_id) setRehydratingGpcSessionId(null);
          adoptPendingRequest((thread as any).pending_request);
        } catch (e: any) {
          if (!isCurrentHydration()) return;
          // A direct MR continuation is already visible. Its transcript read
          // enriches history but must not make that controller unavailable.
          if (initialMrChatView) return;
          if (isMissingBoundWorkflowError(e)) {
            notify('error', USER_FACING_ERROR_MESSAGES.workflow_no_longer_available);
            return;
          }
          if (e instanceof HomeChatHttpError && e.status === 404) {
            clearUnavailableSession(sid);
            notify('info', 'That conversation is no longer available. Start a new one here.');
            return;
          }
          notify('error', chatOpenErrorMessage(e));
          // Don't fire the turn if we couldn't even hydrate the thread.
          return;
        }

        // 2) If we also have a priming message, fire the turn against
        //    /sessions/{sid}/turn (NOT /new/turn). We post directly here
        //    rather than calling `handleSend`, because `handleSend`'s
        //    closure may still see the previous `activeSessionId` (the
        //    `setActiveSessionId(sid)` above only takes effect on the
        //    next render).
        if (msg && msg.trim()) {
          await runPrimingTurn(sid, msg);
        }
        return;
      }

      // No initialSessionId; just an initialMessage → use the lazy
      // /sessions/new/turn path. `handleSend` handles this exact case
      // when `activeSessionId` is null.
      if (msg && msg.trim()) {
        await runPrimingTurn('new', msg);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatOpenErrorMessage, clearUnavailableSession, initialConstructionSowChatView, initialMessage, initialMrChatView, initialSessionId, sessionsLoading, workflowOnly]);

  /**
   * Insert the client-only ``PENDING_ID`` "New Chat" placeholder row at the
   * top of the rail and adopt it as the active session — the SAME optimistic
   * sidebar behaviour the "+ New chat" button uses. Factored out so BOTH
   * entry paths converge on it:
   *   - ``handleNewChat`` (the explicit "+ New chat" click), and
   *   - ``handleSend`` on the homepage / no-session state (a question typed
   *     DIRECTLY into the query bar), so the rail shows "New Chat" INSTANTLY
   *     on submit instead of only after the lazy-create + merge/refresh lands.
   *
   * Double-entry guard: if a ``PENDING_ID`` placeholder already exists (e.g.
   * the user DID click "+ New chat" first, then typed), this is a no-op — it
   * neither inserts a second row nor re-adopts (same-value setState is a
   * React no-op anyway, but the rail de-dup makes the intent explicit).
   *
   * The merge ref (``activeSessionIdRef``) is synced synchronously so a
   * ``refreshSessions`` in flight (or fired right after) re-inserts the
   * placeholder rather than dropping it under the server zero-turn filter.
   */
  const insertPendingPlaceholder = useCallback(() => {
    setMrObserverNotificationId(null);
    setMrNotificationBoundResumeId(null);
    activeSessionIdRef.current = PENDING_ID;
    setActiveSessionId((prev) => {
      // Already on a placeholder — re-adopting is a no-op.
      if (prev === PENDING_ID) return prev;
      return PENDING_ID;
    });
    setSessions((prev) => {
      // De-dup: if a placeholder is already at the top, keep it.
      if (prev.some((s) => s.session_id === PENDING_ID)) return prev;
      const nowIso = new Date().toISOString();
      const placeholder = {
        session_id: PENDING_ID,
        title: null,
        created_at: nowIso,
        last_activity_at: nowIso,
        message_count: 0,
      };
      return [placeholder, ...prev];
    });
  }, []);

  const handleNewChat = useCallback(() => {
    // Lazy-create: clicking "+ New chat" is purely client-side. We insert
    // a `PENDING_ID` placeholder at the top of the rail and wait for the
    // first content action (send / attach / drop) to actually POST to
    // /sessions. This avoids creating orphan empty rows on the server
    // when the user clicks New Chat then walks away.
    interactionRevisionRef.current += 1;
    setMessages([]);
    setAttachments([]);
    setTurns([]);
    setChatCards([]);
    setWorkflowOnlyPurchaseGuidance(null);
    cardActionRevisionRef.current += 1;
    workflowRecommendationRequestIdRef.current = null;
    setBusyWorkflowRecommendationCardId(null);
    setBusySimilarRequirementsCardId(null);
    clearPendingIntentChoiceSelection();
    setGpcChatView(null);
    setPendingGpcRouter(null);
    setMrChatView(null);
    setConstructionSowChatView(null);
    setServicesPwsChatView(null);
    setRehydratingGpcSessionId(null);
    // Reset every session-scoped in-flight signal so an in-progress turn
    // from the previous session can NOT leak its user bubble or
    // StepProgressChecklist into the fresh placeholder. Without this,
    // clicking "+ New chat" while Q1 was still in flight left
    // `inFlightUserMsg='Q1'` and `pendingRequestId=crid` populated; the
    // right pane kept rendering Q1's optimistic bubble + checklist under
    // the empty S2 placeholder, making the two sessions look conflated.
    // FULL teardown via the authoritative shared reset (navigation →
    // clears `pendingClarification` too). `pendingRequestId` uses the
    // ref-syncing setter so the stale-resolve ref is cleared (navigation
    // IS the staleness signal).
    resetTransientTurnState({
      setInFlight,
      clearPendingRequestId: () => setPendingRequestIdSynced(null),
      setInFlightUserMsg,
      setPendingClarification,
      setInFlightClarification,
      setPendingTopicClarification,
      setInFlightTopicFocus,
    });
    // Insert the optimistic "New Chat" rail placeholder + adopt it as the
    // active session (shared with the direct query-bar submit path). The
    // merge ref is synced synchronously inside the helper so a
    // `refreshSessions` in flight (or fired right after) re-inserts the
    // PENDING placeholder rather than dropping it.
    insertPendingPlaceholder();
    // Focus the textarea inside MessageInput.
    const ta = inputContainerRef.current?.querySelector('textarea');
    if (ta) (ta as HTMLTextAreaElement).focus();
  }, [setPendingRequestIdSynced, insertPendingPlaceholder, clearPendingIntentChoiceSelection]);

  /**
   * Resolve to a real (server-issued) session id. If `activeSessionId`
   * is already real, return it. If the user is sitting on the
   * `PENDING_ID` placeholder, POST /sessions exactly once (deduped via
   * `pendingCreatePromiseRef`), adopt the new id, and drop the
   * placeholder from the rail.
   *
   * Used by both `handleSend` and `handleAttach` so a single helper owns
   * the lazy-create transition.
   */
  const ensureSession = useCallback(async (): Promise<string> => {
    if (activeSessionId && activeSessionId !== PENDING_ID) {
      return activeSessionId;
    }
    if (pendingCreatePromiseRef.current) {
      return pendingCreatePromiseRef.current;
    }
    const p = (async () => {
      const { session_id } = await createSession();
      // Sync the merge ref synchronously so the `refreshSessions` below
      // keeps the just-created (still-turn-less) session visible in the
      // rail instead of letting the server zero-turn filter drop it.
      activeSessionIdRef.current = session_id;
      setActiveSessionId(session_id);
      // Swap the client-only placeholder for the real (still-turn-less)
      // session id IN PLACE — keep its position and any timestamps — so the
      // rail row never flashes empty during the lazy-create transition.
      // `refreshSessions`'s active-merge then preserves it (the server hides
      // zero-turn sessions) until the turn persists and the title lands.
      setSessions((prev) => {
        const nowIso = new Date().toISOString();
        const placeholder = prev.find((s) => s.session_id === PENDING_ID);
        const realEntry: ChatSession = {
          session_id,
          title: placeholder?.title ?? null,
          created_at: placeholder?.created_at ?? nowIso,
          last_activity_at: placeholder?.last_activity_at ?? nowIso,
          message_count: placeholder?.message_count ?? 0,
        };
        if (placeholder) {
          return prev.map((s) =>
            s.session_id === PENDING_ID ? realEntry : s
          );
        }
        // No placeholder present (e.g. eager create with no prior click) —
        // prepend so the new session is visible immediately.
        return prev.some((s) => s.session_id === session_id)
          ? prev
          : [realEntry, ...prev];
      });
      void refreshSessions();
      return session_id;
    })();
    pendingCreatePromiseRef.current = p;
    try {
      return await p;
    } finally {
      pendingCreatePromiseRef.current = null;
    }
  }, [activeSessionId, refreshSessions]);

  /**
   * Re-fetch the active thread; used by the SSE `complete` handler so
   * a turn that finished server-side after a tab reload renders into
   * the panel without a manual refresh.
   */
  const refreshActiveThread = useCallback(async () => {
    if (!activeSessionId || activeSessionId === PENDING_ID) return;
    const sessionId = activeSessionId;
    const isCurrentRefresh = () => activeSessionIdRef.current === sessionId;
    try {
      const thread = await getThread(sessionId);
      if (!isCurrentRefresh()) return;
      hydrateDocumentHistory(thread, sessionId);
      const serverMessages = thread.messages || [];
      const serverRawTurns = (thread.turns || []) as RawV2Turn[];
      const serverTurns = serverRawTurns.map((t) => normalizeV2Turn(t));
      // Race protection (priming + lazy-create case): the in-flight POST
      // /sessions/new/v2/turn returns ``{ session_id, turn }`` synchronously
      // and we `setTurns([turn])` + `setActiveSessionId(newSid)` immediately.
      // useTurnProgress's `done=true` poll can then fire
      // `handleProgressComplete` against a freshly-allocated session
      // whose `/thread` endpoint may not yet reflect the persisted turn.
      // If we blindly replaced local state with the server's empty
      // `messages`/`turns`, the chat pane would go BLANK (activeSessionId
      // set, but no messages/turns to render, and the empty-state
      // placeholder is suppressed because activeSessionId is non-null).
      // Guard by keeping local state when the server response is
      // strictly smaller than what we already have.
      setMessages((prev) =>
        serverMessages.length >= prev.length ? serverMessages : prev
      );
      setAttachments(coerceAttachments(thread.attachments));
      setTurns((prev) =>
        serverTurns.length >= prev.length ? serverTurns : prev
      );
      const hasMrBinding = await hydrateMrBinding(
        thread,
        sessionId,
        isCurrentRefresh,
      );
      if (!isCurrentRefresh()) return;
      const hasServicesPwsBinding = !hasMrBinding
        && await hydrateServicesPwsBinding(thread, sessionId, isCurrentRefresh);
      if (!isCurrentRefresh()) return;
      const hasConstructionSowBinding = !hasMrBinding && !hasServicesPwsBinding
        && await hydrateConstructionSowBinding(
          thread,
          sessionId,
          isCurrentRefresh,
        );
      if (!isCurrentRefresh()) return;
      const hasBoundController = hasMrBinding || hasServicesPwsBinding || hasConstructionSowBinding;
      setChatCards(
        hasBoundController && !preserveControllerCards && !hasMrBinding ? [] : normalizeHomeChatCards(thread.chat_cards),
      );
      hydrateGpcState(thread, hasBoundController);
    } catch {
      // Suppressed: a refresh failure shouldn't surface as a hard error
      // because the SSE complete event already terminated the spinner.
    }
  }, [
    activeSessionId,
    hydrateConstructionSowBinding,
    hydrateServicesPwsBinding,
    hydrateGpcState,
    hydrateMrBinding,
    hydrateDocumentHistory,
    preserveControllerCards,
  ]);

  const handleProgressComplete = useCallback(() => {
    // §4.1 multi-turn state hygiene — IN-FLIGHT teardown via the authoritative
    // shared reset. Tears down the in-flight UI state so the
    // StepProgressChecklist bubble does not linger below the answer with all
    // rows marked done, and clears the in-flight user bubble + clarification
    // callout (the newly-fetched ``turns`` array now carries ``user_msg`` /
    // ``clarification``, rendered via ``FarChatResponse`` — no duplicate).
    //
    // DELIBERATELY PRESERVES `pendingClarification` / `pendingTopicClarification`:
    // the backend writes `done=true` (via `complete_turn`) BEFORE returning the
    // post-reader topic-focus / clarification envelope, so the progress poller
    // fires `onComplete` with the pending card just set. A full teardown here
    // would clear it and the TopicFocusCard / ClarificationCard would never
    // render. On the normal (non-card) completion path those pending fields are
    // already null, so this is equivalent to the full reset.
    //
    // `pendingRequestId` uses the PLAIN setter (not the ref-syncing one):
    // progress-complete is a normal turn-lifecycle signal, NOT navigation.
    // If we cleared the stale-resolve ref here, a progress poll that reports
    // done BEFORE the in-flight POST resolves (out-of-order, e.g. a slow
    // lazy-create) would make the POST resolution look "stale" and the guard
    // would drop the just-arrived turn. The ref is cleared only by the
    // navigation teardown handlers (the actual staleness signal).
    resetInFlightTurnState({
      setInFlight,
      clearPendingRequestId: () => setPendingRequestId(null),
      setInFlightUserMsg,
      setInFlightClarification,
      setInFlightTopicFocus,
    });
    setReplacingTurnId(null);
    void refreshActiveThread();
    // Re-fetch the sessions list so the rail picks up the LLM-generated
    // title that the backend's title-summarizer task wrote in parallel
    // with the turn dispatch. The summarizer runs async (best-effort,
    // up to ~5s) so the immediate refresh after the turn HTTP response
    // can race ahead of the rename. Refresh once now (some titles land
    // promptly via Langroid + a fast model) and once again on a brief
    // delay so a slower summarizer still surfaces the new title without
    // the user manually reloading.
    void refreshSessions();
    setTimeout(() => {
      void refreshSessions();
    }, 2500);
  }, [refreshActiveThread, refreshSessions]);

  // MR owns a distinct, owner-bound controller progress resource. Never let
  // the shared v2-turn poller treat the Home request id (or any rehydrated
  // controller identifier) as `/api/home-chat/progress/:id`; doing so both
  // requests the wrong endpoint and masks the MR operation supplied by the
  // controller view.
  const genericProgressRequestId = mrChatView || pendingIntentChoiceSelection?.path === 'mr'
    ? null
    : pendingRequestId;
  const progress = useTurnProgress(genericProgressRequestId, {
    surface: 'home-chat',
    baseUrl: process.env.REACT_APP_API_URL || '',
    onComplete: handleProgressComplete,
  });

  // GPC controller creation is initiated through the same Home Chat request
  // that owns `pendingRequestId`.  Prefer the server's currently-running
  // phase while the controller view is still pending; retain a clear
  // preparation message only until that first progress snapshot arrives.
  const gpcHandoffProgressLabel = useMemo(() => {
    const current = progress.current_step_id
      ? progress.steps.find((step) => step.step_id === progress.current_step_id && step.state === 'running' && step.label.trim())
      : undefined;
    return current?.label
      || progress.steps.find((step) => step.state === 'running' && step.label.trim())?.label
      || 'Starting your GPC purchase…';
  }, [progress.current_step_id, progress.steps]);

  /**
   * Apply the `pending_request` envelope on a thread response: if the
   * backend says a turn is still in flight (started < 10 min ago)
   * reattach the StepProgressChecklist by setting `pendingRequestId`
   * and `inFlight=true`. Otherwise clear both.
   */
  const adoptPendingRequest = useCallback(
    (
      pr:
        | {
            client_request_id?: string;
            started_at?: string;
            user_msg?: string | null;
          }
        | null
        | undefined
    ) => {
      if (!pr || !pr.client_request_id) {
        setPendingRequestIdSynced(null);
        setInFlightUserMsg(null);
        return;
      }
      const startedAtMs = Date.parse(pr.started_at || '');
      const RECENT_WINDOW_MS = 10 * 60 * 1000;
      if (
        !Number.isFinite(startedAtMs) ||
        Date.now() - startedAtMs < RECENT_WINDOW_MS
      ) {
        setPendingRequestIdSynced(pr.client_request_id);
        setInFlight(true);
        // Rehydrate the in-flight user bubble from the persisted
        // ``user_msg`` so the question renders just above the
        // StepProgressChecklist (chronologically AFTER prior turns).
        // Legacy envelopes without this field fall back to ``null``.
        setInFlightUserMsg(pr.user_msg ?? null);
      } else {
        setPendingRequestIdSynced(null);
        setInFlightUserMsg(null);
      }
    },
    [setPendingRequestIdSynced]
  );

  const handleSelectSession = useCallback(
    async (id: string) => {
      interactionRevisionRef.current += 1;
      const navigationRevision = interactionRevisionRef.current;
      const isCurrentNavigation = () => (
        interactionRevisionRef.current === navigationRevision
        && activeSessionIdRef.current === id
      );
      // If the user was on the client-only placeholder and switched
      // away, drop the placeholder from the rail. No server DELETE is
      // needed because no server row was ever created.
      setSessions((prev) =>
        prev.some((s) => s.session_id === PENDING_ID)
          ? prev.filter((s) => s.session_id !== PENDING_ID)
          : prev
      );
      // A reopening cardholder should keep the last durable GPC transcript
      // and form in view while the authoritative thread is refreshed. Do not
      // reuse a different session's projection: only the selected session's
      // own cached server view is eligible.
      const cachedGpcView = gpcViewsByHomeSessionRef.current.get(id) || null;
      // The sessions list carries the durable GPC binding. On a cold mount it
      // tells us to show a GPC loading shell before `/thread` returns the
      // owner-authorized controller transcript and pending form.
      const knownGpcSession = sessionsRef.current.some((session) => (
        session.session_id === id && (
          Boolean(session.gpc_binding?.gpc_chat_session_id)
          || hasConstrainedGpcBinding(session)
        )
      ));
      const switchedSessions = activeSessionIdRef.current !== id;
      setGpcChatView(cachedGpcView);
      setMrChatView(null);
      if (switchedSessions) {
        setMrObserverNotificationId(null);
        setMrNotificationBoundResumeId(null);
      }
      setConstructionSowChatView(null);
      setServicesPwsChatView(null);
      setRehydratingGpcSessionId(cachedGpcView || knownGpcSession ? id : null);
      activeSessionIdRef.current = id;
      setActiveSessionId(id);
      setMessages([]);
      setAttachments([]);
      setTurns([]);
      setChatCards([]);
      setWorkflowOnlyPurchaseGuidance(null);
      cardActionRevisionRef.current += 1;
      workflowRecommendationRequestIdRef.current = null;
      setBusyWorkflowRecommendationCardId(null);
      setBusySimilarRequirementsCardId(null);
      setPendingGpcRouter(null);
      clearPendingIntentChoiceSelection();
      // Switching sessions abandons any in-flight turn on the previous
      // session — none of its transient state may leak into the new pane:
      // the in-flight clarification, the optimistic user bubble (without
      // this a user who switches WHILE a turn is in flight would see that
      // question bubble leak in until adoptPendingRequest overwrites it),
      // and `inFlight` itself (the previous session's turn is no longer
      // relevant; we re-derive it from the freshly-hydrated thread below).
      // FULL teardown via the authoritative shared reset; `pendingRequestId`
      // uses the ref-syncing setter because navigation IS the staleness
      // signal (a late-resolving turn from the old session must no-op).
      resetTransientTurnState({
        setInFlight,
        clearPendingRequestId: () => setPendingRequestIdSynced(null),
        setInFlightUserMsg,
        setPendingClarification,
        setInFlightClarification,
        setPendingTopicClarification,
        setInFlightTopicFocus,
      });
      if (id === PENDING_ID) return;
      try {
        const thread = await getThread(id);
        if (!isCurrentNavigation()) return;
        hydrateDocumentHistory(thread, id);
        const hydratedMessages = thread.messages || [];
        setMessages(hydratedMessages);
        setAttachments(coerceAttachments(thread.attachments));
        // Rehydrate v2 turns from the persisted ``turns`` array. The
        // backend serializes each entry through the same helper used
        // by POST /v2/turn so we can normalize identically.
        const rawTurns = (thread.turns || []) as RawV2Turn[];
        setTurns(rawTurns.map((t) => normalizeV2Turn(t)));
        setBranchRevision(
          Number.isSafeInteger((thread as { branch_revision?: unknown }).branch_revision)
            ? (thread as { branch_revision: number }).branch_revision
            : 0,
        );
        const hasMrBinding = await hydrateMrBinding(
          thread,
          id,
          isCurrentNavigation,
        );
        if (!isCurrentNavigation()) return;
        const hasServicesPwsBinding = !hasMrBinding
          && await hydrateServicesPwsBinding(thread, id, isCurrentNavigation);
        if (!isCurrentNavigation()) return;
        const hasConstructionSowBinding = !hasMrBinding && !hasServicesPwsBinding
          && await hydrateConstructionSowBinding(
            thread,
            id,
            isCurrentNavigation,
          );
        if (!isCurrentNavigation()) return;
        const hasBoundController = hasMrBinding || hasServicesPwsBinding || hasConstructionSowBinding;
        const constrainedChoice = workflowOnly
          ? constrainedWorkflowChoiceFromThread(thread)
          : null;
        const constrainedGuidance = workflowOnly ? constrainedPurchaseGuidanceFromThread(thread) : null;
        if (workflowOnly && thread.constrained_session) {
          setWorkflowOnlyChoice(constrainedChoice?.card || null);
          setWorkflowOnlyPurchaseGuidance(constrainedGuidance?.card || null);
          if (constrainedChoice?.entryMessage || constrainedGuidance?.entryMessage) {
            setWorkflowOnlyEntryMessage(constrainedChoice?.entryMessage || constrainedGuidance?.entryMessage || null);
          }
        }
        setChatCards(
          (hasBoundController && !preserveControllerCards && !hasMrBinding) || (workflowOnly && !hasMrBinding && !constrainedGuidance && !constrainedChoice)
            ? [] : normalizeHomeChatCards(thread.chat_cards),
        );
        restorePendingClarification(thread, id);
        // `gpc_chat` is resolved by the backend from the durable binding and
        // is returned only after owner authorization; no client cache or flow
        // id is used to restore this controller.
        hydrateGpcState(thread, hasBoundController);
        // Only the server's explicit pending envelope can rehydrate an
        // active request. Message/card/turn counts are not a liveness
        // signal: their response may be persisted in a different record.
        adoptPendingRequest((thread as any).pending_request);
      } catch (e: any) {
        if (!isCurrentNavigation()) return;
        if (isMissingBoundWorkflowError(e)) {
          notify('error', USER_FACING_ERROR_MESSAGES.workflow_no_longer_available);
          return;
        }
        if (e instanceof HomeChatHttpError && e.status === 404) {
          clearUnavailableSession(id);
          notify('info', 'That conversation is no longer available. Start a new one here.');
          return;
        }
        notify('error', chatOpenErrorMessage(e));
      } finally {
        if (activeSessionIdRef.current === id) setRehydratingGpcSessionId(null);
      }
    },
    [
      notify,
      adoptPendingRequest,
      setPendingRequestIdSynced,
      hydrateConstructionSowBinding,
      hydrateServicesPwsBinding,
      hydrateGpcState,
      hydrateMrBinding,
      clearPendingIntentChoiceSelection,
      clearUnavailableSession,
      chatOpenErrorMessage,
      restorePendingClarification,
      workflowOnly,
      hydrateDocumentHistory,
      preserveControllerCards,
    ]
  );

  return {
    insertPendingPlaceholder,
    handleNewChat,
    ensureSession,
    progress,
    gpcHandoffProgressLabel,
    handleSelectSession,
  };
}

export type HomeChatSessions = ReturnType<typeof useHomeChatSessions>;
